import { useState, useEffect, ReactNode } from "react";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from "./tooltip";
import { useIsMobile } from "@/hooks/use-mobile";

interface TouchTooltipProps {
  children: ReactNode;
  content: ReactNode;
  delayDuration?: number;
  side?: "top" | "right" | "bottom" | "left";
  className?: string;
}

export function TouchTooltip({
  children,
  content,
  delayDuration = 0,
  side,
  className,
}: TouchTooltipProps) {
  const isMobile = useIsMobile();
  const [isOpen, setIsOpen] = useState(false);

  // Handle click outside to close on mobile
  useEffect(() => {
    if (!isMobile || !isOpen) return;

    const handleClickOutside = () => setIsOpen(false);
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [isMobile, isOpen]);

  if (isMobile) {
    // Mobile: Controlled tooltip with click handler
    return (
      <TooltipProvider delayDuration={delayDuration}>
        <Tooltip open={isOpen} onOpenChange={setIsOpen}>
          <TooltipTrigger
            asChild
            onClick={(e) => {
              e.stopPropagation();
              setIsOpen(!isOpen);
            }}
          >
            {children}
          </TooltipTrigger>
          <TooltipContent side={side} className={className}>
            {content}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  // Desktop: Standard hover behavior
  return (
    <TooltipProvider delayDuration={delayDuration}>
      <Tooltip>
        <TooltipTrigger asChild>{children}</TooltipTrigger>
        <TooltipContent side={side} className={className}>
          {content}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
